<script setup>
    import { SEARCH } from './config';
    import { storeToRefs } from 'pinia';
    import { defineAsyncComponent, inject, nextTick, onMounted, ref, unref } from 'vue';
    import { useSearchStore } from './store/useSearchStore';
    import { useUrlSearchParams, useElementVisibility, watchDebounced } from '@vueuse/core';
    import { useDebug } from '../../composables/useDebug';
    import useLiferayLocale from '../../composables/useLiferayLocale';
    import axios from '../../setup/axios';
    import escapeQuery from '../../helpers/escapeQuery';
    import SearchForm from './components/SearchForm.vue';
    import SearchModeSwitch from './components/SearchModeSwitch';
    import groupBy from 'lodash-es/groupBy';
    import productCsvExport from './helpers/productCsvExport';
    import getParameterHeadings from './helpers/getParameterHeadings';
    import { useI18n } from 'vue-i18n';

    // Lazy load components that we might not need
    const ProductSearchFilters = defineAsyncComponent(() => import('./components/ProductSearchFilters.vue'));
    const SearchResults = defineAsyncComponent(() => import('./components/SearchResults.vue'));
    const FacetsList = defineAsyncComponent(() => import('./components/FacetsList.vue'));
    const Spinner = defineAsyncComponent(() => import('../LoadingSpinner/LoadingSpinner.vue'));
    const ProductCompare = defineAsyncComponent(() => import('./components/ProductCompare'));
    const ActiveFiltersList = defineAsyncComponent(() => import('./components/ActiveFiltersList'));

    const { log } = useDebug();
    const { t } = useI18n();

    const store = useSearchStore();

    const $bus = inject('$bus');

    const params = useUrlSearchParams('history');

    const searchRef = ref(null);
    const searchResultsRef = ref(null);
    const searchResultsStartRef = ref(null);
    const searchResultsContainerStartRef = ref(null);
    const isSearchResultsStartVisible = useElementVisibility(searchResultsStartRef);
    const isSearchResultsContainerVisible = useElementVisibility(searchResultsContainerStartRef);
    const formRef = ref(null);

    const errors = ref([]);
    /** @type {AbortController | null} */
    const abortController = ref(null);

    // Evaluate query parameters on initial load
    onMounted(() => {
        Object.keys(params)
            .filter(param => param.startsWith('filter_'))
            .forEach(filterParam => {
                let key = filterParam.replace('filter_', '');

                let values = params[filterParam].split(',');
                for (let value of values) {
                    store.addFilter({
                        filterKey: key,
                        filterValue: value,
                    });
                }
            });

        if (params.q) {
            store.searchQuery = params.q;
        }
        if (params.productSearch) {
            // Toggle search mode silently to not trigger a search already
            store.toggleProductSearchMode(true, false, true, true);

            // Trigger a search in the next tick so the store has enough time to update
            nextTick(() => handleSearch());
        } else if (params.q) {
            nextTick(() => handleSearch());
        }

        // Quickfix: Add the debounced watcher here so we do not interfere with the already running product search
        // TODO: there has to be a cleaner solution for this
        nextTick(() => {
            const { searchQuery } = storeToRefs(store);
            watchDebounced(
                searchQuery,
                query => {
                    log?.(store.searchQuery, query);
                    handleSearch(query);
                },
                {
                    debounce: SEARCH.SEARCH_WAIT_TIMEOUT,
                    maxWait: 5000,
                }
            );
        });
    });

    /**
     * Handle search result page change.
     *
     * @param {number} page
     */
    const handleResultPageChange = page => {
        handleSearch(null, page);
    };
    $bus.on('search:result-page-switched', handleResultPageChange);

    /**
     * Update the selected facets and fire a search request.
     *
     * @param {object} facet
     * @returns {void}
     */
    const handleSelectedFacetsUpdated = facet => {
        store.toggleSelectedFacet(facet);
    };
    $bus.on('search:selected-facets-updated', handleSelectedFacetsUpdated);
    $bus.on('search:facets-updated', () => {
        log?.('🤙 search:facets-updated');
        handleSearch();
        toggleFilter();
    });
    $bus.on('search:filters-cleared', () => handleSearch());
    $bus.on('search:mode-updated', () => handleSearch());
    $bus.on('search:reset', () => handleSearch());
    $bus.on('search:sorting-updated', () => handleSearch());
    $bus.on('search:export-csv-all', () => {
        handleSearch(store.searchQuery, 1, true);
    });

    /**
     * Handle the search results.
     *
     * @param {Object} responseResult The search result as json object.
     * @param {Boolean} CSVExport Flag if the result will be shown or it is only for the CSV Export.
     * @returns {void}
     */
    const handleResult = (responseResult, CSVExport = false) => {
        const startingIndex = Object.hasOwn(responseResult, 'startIndex') ? responseResult.startIndex : 0;

        /** @type SearchResult[] */
        const results = responseResult.results.map(result => {
            const groupedParameters = groupBy(result.parameters, 'grouping');
            const groupedPriorityParameters = groupBy(result.parameters, 'priority');
            let groupedPrimaryParameters = groupBy(groupedPriorityParameters.primary, 'grouping');
            let groupedSecondaryParameters = groupBy(groupedPriorityParameters.secondary, 'grouping');

            if (groupedPriorityParameters?.primary?.length > 0) {
                groupedPrimaryParameters = groupBy(groupedPriorityParameters.primary, 'grouping');
            }
            if (groupedPriorityParameters?.secondary?.length > 0) {
                groupedSecondaryParameters = groupBy(groupedPriorityParameters.secondary, 'grouping');
            }

            return {
                ...result,
                groupedParameters,
                groupedParameterKeys: Object.keys(groupedParameters),
                groupedPrimaryParameters,
                groupedPrimaryParameterKeys: Object.keys(groupedPrimaryParameters),
                groupedSecondaryParameters,
                groupedSecondaryParameterKeys: Object.keys(groupedSecondaryParameters),
            };
        });
        if (CSVExport) {
            productCsvExport(results, getParameterHeadings(results), t);
        } else {
            store.$patch({
                results: results,
                totalResults: Object.hasOwn(responseResult, 'totalCount')
                    ? responseResult.totalCount
                    : responseResult.results.length,
                productTypes: responseResult.productTypes,
                currentPage: Math.ceil(startingIndex / SEARCH.RESULTS_PER_PAGE) + 1,
                facets: store.extractFacets(responseResult.aggregations),
                rangeSliders: store.extractRangeSliderValues(responseResult.aggregations),
            });

            // params.q = store.searchQuery;
            // params.productSearch = store.isProductSearchMode ? 'true' : null;

            formRef?.value?.inputRef?.value?.inputRef?.value?.focus();
            document.body.classList.remove('has-scrolllock');

            nextTick(() => {
                store.updateUrlParameters();

                if (searchResultsStartRef.value && !isSearchResultsStartVisible.value) {
                    searchResultsStartRef.value?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start',
                        inline: 'nearest',
                    });
                }
            });
        }

        store.$patch({
            busy: false,
        });
    };

    /**
     * Reset the cancel callback and busy states.
     *
     * @returns {void}
     */
    const resetRequest = () => {
        abortController.value = null;
    };

    /**
     * Handle a search request.
     *
     * @param {null | string} customSearchQuery The search query, defaults to the store value.
     * @param {number} page The page number to go to.
     * @param {boolean} allProducts Flag for the CSV Export
     * @returns {void}
     */
    const handleSearch = (customSearchQuery = null, page = 1, allProducts = false) => {
        const query = (customSearchQuery || store.searchQuery)?.trim() || '';
        let resultsPerPage = SEARCH.RESULTS_PER_PAGE;
        if (allProducts) {
            resultsPerPage = 99999;
            page = 1;
            log?.('All products for CSV Export.');
        } else {
            log?.('Search.');
            page = page > 1 ? page : 1;
        }

        log?.('productSearch: ', store.isProductSearchMode);

        if (query.length === 0) {
            store.searchQuery = null;

            if (!store.isProductSearchMode) {
                store.facets = null;
            }
        }

        // Fire search request only for queries with lengths greater than or equal
        // to the minimum required length.
        if (store.isProductSearchMode || query.length >= SEARCH.MINIMUM_SEARCH_QUERY_LENGTH) {
            log?.('🔎️ searching for: ', query, page);

            // Set status to busy (shows spinner)
            store.busy = true;

            // Cancel previous request
            if (abortController.value) {
                abortController.value.abort();
            }
            abortController.value = new AbortController();

            const { cldrLocale } = useLiferayLocale();

            // console.log(store.prepareAggregationsForPayload());

            // Post request to the search interface
            axios
                .post(
                    '/o/ams-osram-search/search',
                    {
                        keywords: escapeQuery(query),
                        currentPage: page,
                        pageSize: resultsPerPage,
                        aggregations: store.prepareAggregationsForPayload(),
                        filters: store.filters,
                        sortFieldName: store.sortFieldName,
                        sortOrder: store.sortOrder,
                        productSearch: store.isProductSearchMode,
                        locale: unref(cldrLocale),
                        searchGroupIds: [window.Liferay?.ThemeDisplay?.getSiteGroupId?.() || null],
                    },
                    {
                        responseType: 'json',
                        signal: abortController.value.signal,
                    }
                )
                .then(response => {
                    log?.('🕵️‍ Search response:', response);
                    handleResult(response.data, allProducts);
                })
                .catch(e => errors.value.push(e))
                .finally(() => resetRequest());
        }
    };

    const toggleFilter = () => {
        store.toggleFacetsListOpen(true);
    };

    const jumpToResults = () => {
        if (searchResultsContainerStartRef.value && !isSearchResultsContainerVisible.value) {
            searchResultsContainerStartRef.value?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
                inline: 'nearest',
            });
        }
    };
</script>

<template>
    <div
        ref="searchRef"
        :class="[
            'search__wrapper',
            {
                'is-searching': store.busy,
                'is-product-search': store.isProductSearchMode,
                'has-search-mode-question': store.shouldOfferProductSearch,
                'is-expanded-view': store.isExpandedView,
            },
        ]"
    >
        <div
            class="search__form"
            :class="{
                'is-product-search': store.isProductSearchMode,
                'has-search-mode-question': store.shouldOfferProductSearch,
            }"
        >
            <SearchForm ref="formRef" />
            <SearchModeSwitch v-if="store.shouldOfferProductSearch" />
            <ProductSearchFilters v-if="store.isProductSearchMode" />
            <ActiveFiltersList />
            <ProductCompare v-if="store.isCompareVisible" />
        </div>

        <teleport to="#wrapper">
            <div v-if="store.busy" class="search-loading-spinner has-text-centered">
                <Spinner class="search__spinner" :size="'large'" :line-fg-color="'#46555f'" />
            </div>
        </teleport>

        <span ref="searchResultsStartRef" />

        <button
            v-if="store.facetsListOpen && !isSearchResultsContainerVisible"
            class="button jump-button"
            @click="jumpToResults"
        >
            {{ $t('search_jumpButton') }}
            <svg class="icon chevron-right">
                <use xlink:href="#chevron-right"></use>
            </svg>
        </button>

        <div
            v-if="store.hasResults"
            ref="searchResultsRef"
            :class="['has-default-search-grid container', { 'is-expanded-view': store.isExpandedView }]"
        >
            <FacetsList />
            <div ref="searchResultsContainerStartRef">
                <SearchResults />
            </div>
        </div>
        <div
            v-if="
                !store.busy &&
                !store.hasResults &&
                (store.isProductSearchMode || (store.searchQuery != null && store.searchQuery.length >= 1))
            "
            class="container"
        >
            <p class="empty search__empty">
                <span class="empty__heading">{{ $t('search_oops') }}</span>
                <span class="empty__text">{{ $t('search_noResults') }}</span>
            </p>
        </div>
    </div>
</template>

<style lang="scss">
    @use 'sass:list';
    @import '../../../scss/variables';
    @import '../../../scss/utilities/all';
    @import '../../../scss/elements/title';

    // START fix for position sticky
    html,
    body {
        min-height: 100vh;
        overflow: unset;
    }
    // END fix for position sticky

    .page-search .page-content > .breadcrumbs {
        display: none;
    }

    .search-loading-spinner {
        position: fixed;
        z-index: 100;
        top: 50%;
        left: 50%;
        transform: translateX(-50%) translateY(-50%);
        display: flex;
        padding: 1rem;
        border-radius: 1rem;
        backdrop-filter: blur(6px);
        background-color: rgba(0, 0, 0, 0.025);
    }

    .search {
        &__wrapper {
            min-height: calc(100vh - 4.5rem - 3.25rem); // -navigation -breadcrumb
            text-align: left;

            .jump-button {
                background-color: $secondary;
                border-radius: 4px;
                padding: 0.5rem 1rem;
                gap: 0.5rem;
                color: $white;
                width: auto;
                position: fixed;
                z-index: 200;
                bottom: 1rem;
                right: 1rem;

                @include desktop {
                    display: none;
                }

                .chevron-right {
                    transform: rotate(90deg);
                    color: $white;
                    width: 10px;
                    height: 10px;
                }
            }

            .container {
                display: flex;
                gap: 1rem;
                position: relative;
                justify-content: space-between;
                width: 100%;
                transition: width 2s;

                &.is-expanded-view {
                    min-width: 98%;
                    width: 50%;
                }

                @include mobile {
                    padding: 0;
                }

                &.has-default-search-grid {
                    display: grid;
                    grid-template-columns: 20rem 1fr;
                    grid-template-rows: auto;
                    grid-gap: 1.5rem;
                    position: relative;

                    @include mobile {
                        grid-template-columns: 1fr;
                    }
                }

                &.has-link {
                    display: block;
                    padding: 2.5rem 0 0;
                    color: rgba($white, 0.8);
                    font-size: 1rem;

                    a {
                        display: inline-flex;
                        gap: 0.5rem;
                        align-items: center;
                        justify-content: center;
                        color: rgba($white, 0.8);
                        font-weight: $weight-semibold;
                        text-decoration: underline;

                        &:hover {
                            color: rgba($white, 1);
                            text-decoration: none;
                        }
                    }
                }
            }

            .link-container {
                display: none;

                @include desktop {
                    display: flex;
                }

                .has-link:nth-child(2) {
                    text-align: right;
                }

                @include mobile() {
                    flex-direction: column;

                    .has-link:nth-child(2) {
                        text-align: left;
                        padding-top: 1rem;
                    }
                }
            }

            > .container {
                margin-bottom: 5rem;
            }
        }

        &__form {
            width: 100%;
            margin-bottom: 2rem;
            color: $white;
            font-size: $size-3;

            @include mobile {
                margin-bottom: 0px;
            }

            @include tablet-only {
                margin-bottom: 0px;
            }

            .wrapper {
                background-color: $secondary;
                padding: 2rem 0 1.5rem;
            }

            &.has-search-mode-question,
            &.is-product-search {
                padding-bottom: 0;
            }

            .container {
                padding: 0 1rem;
            }
        }

        &__heading {
            display: flex;
            flex: 1 1 auto;

            .search-tooltip {
                position: relative;
                opacity: 1;

                .icon-info {
                    width: 1.5rem;
                    height: 1.5rem;
                }

                .tooltip {
                    display: none;
                    position: absolute;
                    top: 50%;
                    right: 100%;
                    width: auto;
                    min-width: 200px;
                    max-width: 600px;
                    margin-top: -5px;
                    border-width: 1px;
                    border-style: solid;
                    border-color: #c8cacc;
                    background-color: #dfe1e3;

                    ul {
                        margin: 0;
                        padding: 15px;
                        padding-left: 30px;
                    }
                }

                &:hover {
                    .tooltip {
                        display: block;
                        visibility: visible;
                        opacity: 1;
                    }
                }
            }
        }

        &__label {
            display: grid;
            grid-template-columns: 1fr;
            grid-template-rows: auto;
            grid-gap: 1.5rem;
            width: 100%;
            color: $white;
            font-size: 1em;
            font-weight: $weight-light;

            @include desktop {
                grid-template-columns: minmax(auto, 20rem) 1fr;
            }
        }

        &__text {
            display: flex;
            justify-content: right;
            align-items: center;

            @include mobile {
                display: none;
                visibility: hidden;
            }
        }

        &__filter-toggle {
            border: 0 none;
            background-color: transparent;
            color: $white;
            cursor: pointer;

            @include mobile {
                display: block;
                flex: 0 0 auto;
            }
            @include tablet {
                display: none;
                visibility: hidden;
            }

            .icon-filter {
                opacity: 0.56;

                @include mobile {
                    width: 1.5rem;
                    height: 1.5rem;
                }
            }
        }

        &__input {
            width: 100%;
            border: none;
            font-size: 1.125rem;
            background: transparent;
            color: $white;
            font-weight: $weight-semibold;

            &,
            &:focus {
                outline: none;
            }

            @include placeholder {
                color: rgba($white, 0.54);
                font-weight: $weight-light;
            }
        }

        &__facets {
            position: -webkit-sticky;
            position: sticky;
            top: 1rem;
            max-width: 320px;
            max-height: 100vh;
            overflow-y: auto;
            align-self: flex-start;

            &.is-product-search {
                margin-top: 3.23rem;
                padding-top: 0.5rem;

                &.is-expanded-view {
                    margin-top: 4.85rem;
                }
            }

            @include mobile {
                position: relative;
                z-index: 100;
                width: calc(100vw - 25px);
                max-width: 400px;
                height: auto;
                max-height: 85vh;
                margin-top: 1rem !important;
            }

            &:not(.is-open) {
                @include mobile {
                    display: none;
                }
            }
        }

        &__results {
            display: flex;
            position: relative;
            flex: 1 1 auto;
            flex-direction: column;
            width: 100%;
            min-height: 420px;

            .product-list & {
                min-height: 0;
            }

            &:not(.is-product-search) {
                gap: 1rem 0;
            }

            &:not(.is-expanded-view) {
                max-width: calc(1420px - 25% - 30px);
            }
        }

        &-results-container {
            &.is-expanded-view {
                overflow-x: auto;
            }

            @include tablet-only {
                overflow-x: auto;
            }
        }

        &__result {
            display: flex;
            flex-direction: column;
            gap: 1rem;
            padding: 1rem;
            transition: opacity $speed ease-in-out;
            background-color: $white;

            &--animated {
                animation-name: fade-in-up;
                animation-duration: 0.4s;
                animation-fill-mode: both;
            }

            .is-searching & {
                opacity: 0.6;
            }

            .button {
                font-size: 0.875rem;
            }
        }

        &__info {
            color: rgba($text, 0.5);
            font-size: 0.75rem;
            text-transform: uppercase;
        }

        &__highlight {
            color: $primary;
            font-style: normal;

            &:before,
            &:after {
                clip-path: inset(100%);
                clip: rect(1px, 1px, 1px, 1px);
                height: 1px;
                overflow: hidden;
                position: absolute;
                white-space: nowrap;
                width: 1px;
            }

            &:before {
                content: ' [highlight start] ';
            }

            &:after {
                content: ' [highlight end] ';
            }
        }

        &__empty {
            margin: 0 auto;
            text-align: center;

            .empty {
                &__heading,
                &__text {
                    display: block;
                }

                &__heading {
                    font-size: 2em;
                    line-height: 1.8;
                }

                &__text {
                    line-height: 1.6;
                }
            }
        }

        &__pagination {
            display: flex;
            gap: 0.5rem;
            align-items: center;
            justify-content: center;
        }
    }

    .facets {
        transition: transform $speed ease-in-out;
        will-change: transform;

        &:not(.is-open) {
            @include mobile {
                transform: translateX(-100%);
            }
        }

        &.is-open {
            @include mobile {
                transform: translateX(0) translateY(0);
            }
        }

        &__wrapper {
            position: relative;
            z-index: 101;
            background-color: $white;

            &.is-normal {
                margin-top: calc(1rem + 16.8px);

                @include mobile {
                    margin-top: calc(0.5rem + 16.8px);
                }
            }

            @include mobile {
                width: calc(100% - 10px);
                height: 100%;
                padding: 25px;
                overflow-x: scroll;
                box-shadow:
                    0 0 1px 2px rgba(0, 0, 0, 0.02),
                    0 8px 24px rgba(0, 0, 0, 0.12);
            }
            @include tablet {
                width: 100%;
                padding: 1rem;
            }

            h4 {
                @extend .title; // stylelint-disable-line

                color: $text;
                font-size: $size-3;
            }

            .button {
                &.is-link {
                    padding: 0;
                    margin-bottom: 1rem;
                    text-align: left;
                }
            }
        }

        &__close {
            border: 0 none;
            opacity: 0.56;
            background-color: transparent;
            cursor: pointer;

            @include mobile {
                display: inline-block;
                position: absolute;
                top: 1.25rem;
                right: 1.25rem;
            }
            @include tablet {
                display: none;
            }

            .icon-remove {
                width: 1.5rem;
                height: 1.5rem;
            }
        }

        &__backdrop {
            display: none;
            position: absolute;
            z-index: 99;
            top: 0;
            left: 0;
            width: 100vw;
            height: 100vh;
            background-color: rgba($white, 0.9);
            backdrop-filter: blur(8px);

            .is-open & {
                @include mobile {
                    // display: block;
                }
            }
        }
    }

    .pagination {
        &__button {
            display: inline-flex;
            flex: 0 0 auto;
            align-items: center;
            justify-content: center;
            height: 2rem;
            padding: 0 1rem;
            border: 0 none;
            border-radius: $radius;

            &:not([disabled]):not(.is-disabled):not(.is-active) {
                cursor: pointer;
            }

            &--prev,
            &--next {
                flex-basis: 1rem;
                padding: 0;
                background: transparent;

                .icon {
                    &,
                    svg {
                        width: 1rem;
                        height: 1rem;
                    }
                }
            }

            &--page {
                background-color: $secondary;
                color: $white;
                font-size: 1rem;

                &:hover {
                    background-color: $secondary-hover;
                }

                &:not(.is-active) {
                    font-weight: $weight-light;
                }

                &.is-active {
                    cursor: default;

                    &,
                    &:hover {
                        background-color: $tertiary;
                    }
                }
            }
        }

        &__ellipsis {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            color: rgba($secondary, 0.5);
            cursor: default;
        }

        &__pages {
            display: flex;
            flex: 0 0 auto;
            gap: 0.5rem;
            flex-wrap: wrap;
            justify-content: center;
        }
    }

    .result {
        &__title {
            @extend .title; // stylelint-disable-line

            display: flex;
            margin: 0;
            font-size: $size-3;

            a {
                color: $text;
            }
        }

        &__icon {
            flex: 0 0 auto;
            width: 48px;
            height: 48px;
            padding-right: 0.4em;
        }

        &__body {
            display: flex;
            flex-wrap: wrap;
            color: $secondary;
            gap: 20px;

            @include mobile {
                display: block;
            }
        }

        &__image,
        &__video {
            flex: 0 1 40%;
            max-width: 40% !important; // stylelint-disable-line declaration-no-important
            overflow: hidden;
            max-height: 200px;

            img {
                aspect-ratio: 16/9;
                object-fit: cover;
            }

            @include mobile {
                width: 100%;
                margin-bottom: 1rem;
                max-width: none !important; // stylelint-disable-line declaration-no-important
            }
        }

        &__video {
            min-height: 200px;
        }

        &__content {
            flex: 1 1;
            word-break: break-word;
            display: flex;
            gap: 20px;
            flex-direction: column;
            align-items: flex-start;

            @include desktop {
                max-width: 60%;
            }

            .button-container {
                display: flex;
                flex-direction: row;
                flex-wrap: wrap;
                gap: 1rem;

                .buy-now {
                    gap: 1rem;
                }
            }
        }

        &__description {
            overflow: hidden;
            text-overflow: ellipsis;
            display: -webkit-box;
            -webkit-line-clamp: 3;
            line-clamp: 3;
            -webkit-box-orient: vertical;

            p {
                &:last-child {
                    margin-bottom: 0;
                }
            }
        }

        &__link {
            display: inline-block;
        }

        a {
            // stylelint-disable-next-line
            &.title__text {
                &:not([href]):not([tabindex]) {
                    color: $primary;
                }
            }
        }
    }

    .title {
        &__content {
            display: flex;
            flex: 1 1 auto;
            flex-direction: column;
            max-width: calc(100% - 40px);
        }

        &__text,
        &__type {
            display: block;
            width: 100%;
        }

        &__type {
            font-size: 0.4em;
            line-height: 2;
        }
    }

    .facet {
        &__header {
            @extend .title; // stylelint-disable-line

            display: flex;
            justify-content: space-between;
            margin: 0;
            padding: 1rem 0;
            border-top: 1px solid rgba($secondary, 0.24);
            color: $text;
            font-size: $size-4;
            font-weight: $weight-normal;
            cursor: pointer;

            .icon {
                transform: rotate(0);

                &,
                svg {
                    width: 1.5rem;
                    height: 1.5rem;
                }

                // stylelint-disable-next-line
                .facet__group--open & {
                    transform: rotate(90deg);
                }
            }
        }

        &__subtitle {
            @extend .title;

            display: block;
            color: $text;
            font-size: $size-6;
            font-weight: $weight-normal;
        }

        &__items {
            margin: 0;
            padding: 0;
            border-top: 1px solid rgba($secondary, 0.24);
            list-style: none;

            ul.facet__items {
                border-top: 0 none;

                &:not(.is-facet-group) {
                    padding-left: 1.5rem;
                }
            }

            &:empty {
                border-top: 0 none;
            }
        }

        &__item {
            display: block;
            margin-top: 1rem;

            &:last-child {
                margin-bottom: 2rem;
            }

            &.has-color,
            &.has-icon {
                .facet__label {
                    gap: 0.5rem;
                }
            }

            &.has-icon {
                .icon {
                    width: 1.5rem;
                    height: 1.5rem;
                }
            }

            &.has-color {
                display: flex;
                gap: 1rem;
                .checkbox__label {
                    position: relative;
                    &:before,
                    &:after {
                        content: '';
                        left: 0;
                        top: 0;
                        width: 1.5rem;
                        height: 1.5rem;
                        display: block;
                    }
                    &:after {
                        position: absolute;
                        z-index: 123;
                        border: 1px solid rgba(70, 85, 95, 0.24);
                    }
                    .facet-name {
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        max-width: 180px;
                    }
                }
                @each $name, $pair in $icon-colors {
                    $background-color: list.nth($pair, 1);
                    &.is-#{$name} {
                        .checkbox__label {
                            &:before {
                                background: $background-color;
                            }
                        }
                    }
                }
            }

            &:has(.slider-facet) {
                margin-bottom: 2rem;
            }
        }

        &__label {
            display: inline-flex;
            position: relative;
            align-items: center;
            margin: 0;
            outline: none;
            font-weight: $weight-light;
            line-height: 1.25;
            cursor: pointer;
            user-select: none;
        }

        &__checkbox {
            flex: 0 0 auto;
        }

        &__name {
            display: block;
            flex: 1 1 auto;
            padding-left: 0.4em;
        }
    }

    .checkbox {
        display: flex;
        gap: 1rem;
        align-items: center;

        &__input {
            position: absolute;
            opacity: 0;
            width: 1px;
            height: 1px;
            margin: 0;
            clip: rect(0 0 0 0);
            overflow: hidden;

            // stylelint-disable-next-line
            + .checkbox__check {
                flex-shrink: 0;
                width: 1.125rem;
                height: 1.125rem;
                transition:
                    background,
                    box-shadow $speed ease-out;
                border: 2px solid rgba($secondary, 0.5);
                border-radius: $radius-small;
            }

            &:checked {
                // stylelint-disable-next-line
                + .checkbox__check {
                    border-color: $secondary;
                    background: $secondary
                        url("data:image/svg+xml;charset=utf-8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1 1'><path d='M.04.627L.146.52.43.804.323.91zm.177.177L.854.167.96.273.323.91z' fill='%23fff'/></svg>")
                        no-repeat 50%;
                }
            }
        }

        &:focus,
        &:hover {
            .checkbox__input {
                + .checkbox__check {
                    box-shadow: 0 0 0 0.25rem rgba($secondary, 0.1);
                }

                &:checked + .checkbox__check {
                    box-shadow: 0 0 0 0.25rem rgba($secondary, 0.2);
                }
            }
        }

        &:focus .checkbox__input + .checkbox__check {
            outline: 0.125rem solid $primary;
            outline-offset: 0.125rem;
        }

        &__label {
            display: inline-flex;
            gap: 0.5rem;
            align-items: center;

            .facet-name {
                hyphens: auto;
            }

            .facet-count {
                white-space: nowrap;
            }
        }

        .quiet {
            color: rgba($secondary, 0.5);
        }
    }

    .animated {
        animation-duration: 0.4s;
        animation-fill-mode: both;
    }

    .items {
        z-index: 1;
        backface-visibility: hidden;
    }

    .items-move {
        transition: all $speed ease-out 50ms;
    }

    .items-enter-active {
        transition: all $speed ease-out;
    }

    .items-leave-active {
        position: absolute;
        z-index: 0;
        transition: all $speed ease-in;
    }

    .items-enter,
    .items-leave-to {
        opacity: 0;
    }
</style>
